import React from "react";
import Seo from "../components/Seo";
import { Section, PageHero } from "../components/primitives";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Mail, Handshake, LifeBuoy, ArrowUpRight } from "lucide-react";
import { SUPPORT_EMAIL, PARTNERSHIP_EMAIL, IMAGES } from "../lib/content";

const cards = [
  {
    icon: LifeBuoy,
    label: "General Support",
    title: "Help, accounts & general questions",
    desc: "App support, account and membership questions, privacy and data-protection requests, account deletion, or anything else — our team is here to help.",
    email: SUPPORT_EMAIL,
    testid: "contact-support-email",
    featured: true,
  },
  {
    icon: Handshake,
    label: "Business Partnerships",
    title: "Device, integration & strategic partnerships",
    desc: "Device manufacturers, wearable and connected fitness brands exploring Bluetooth compatibility, API/SDK, OEM, distribution or strategic partnerships.",
    email: PARTNERSHIP_EMAIL,
    testid: "contact-partnership-email",
    featured: false,
  },
];

export default function Contact() {
  return (
    <div>
      <Seo
        title="Contact"
        path="/contact"
        description="Contact Jollof Fit Kingdom. General support and account questions: jolloffitkingdom@gmail.com. Business partnerships: partnerships.jolloffit@gmail.com."
        noindex
      />
      <PageHero
        eyebrow="Contact"
        title="Let's start a"
        highlight="conversation"
        description="Reach the right team directly. General support is handled separately from business partnerships — choose the one that fits your message."
      />

      <Section className="mt-4">
        <Stagger className="grid gap-6 lg:grid-cols-2">
          {cards.map((c) => (
            <StaggerItem key={c.email}>
              <div
                className={`relative flex h-full flex-col overflow-hidden rounded-3xl border p-8 md:p-10 ${
                  c.featured
                    ? "border-gold/25 bg-gradient-to-b from-[#14110a] to-[#080808]"
                    : "border-white/10 bg-[#0a0a0a]"
                }`}
              >
                {c.featured && (
                  <div className="pointer-events-none absolute -top-24 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-gold/10 blur-3xl" />
                )}
                <div className="relative">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-gold">
                    <c.icon className="h-5 w-5" strokeWidth={1.6} />
                  </div>
                  <p className="mt-6 text-xs font-bold uppercase tracking-[0.25em] text-gold">{c.label}</p>
                  <h2 className="mt-3 font-heading text-2xl font-medium tracking-tight text-white">{c.title}</h2>
                  <p className="mt-4 text-sm leading-relaxed text-zinc-400">{c.desc}</p>
                  <a
                    href={`mailto:${c.email}`}
                    data-testid={c.testid}
                    className="group mt-8 inline-flex items-center gap-3 rounded-full border border-gold/40 bg-gold/10 px-6 py-3.5 font-heading text-sm text-gold-light transition-colors duration-300 hover:bg-gold/20 md:text-base"
                  >
                    <Mail className="h-5 w-5" /> {c.email}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal delay={0.1}>
          <div className="mt-10 flex items-center gap-4 rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
            <img src={IMAGES.jfLogo} alt="Jollof Fit Kingdom" className="h-12 w-12 shrink-0 rounded-full" />
            <p className="text-sm leading-relaxed text-zinc-400">
              We read every message and typically respond within a few business days. For account deletion requests,
              please email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:text-gold-light">
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
